/**
 * Página pública /accept-platform-invitation/:invitationId
 *
 * Variante reducida de AcceptInvitationPage.tsx (invitación de TENANT) para
 * el plano PLATAFORMA (ADR 0012 — Fase 1). Solo RAMA A (signup-required):
 * un user preexistente con el email es un error de estado (409 USER_EXISTS),
 * no un camino alternativo — ver ADR 0012 §Race con user preexistente.
 *
 * Modos del preview:
 *   - "signup-required" → formulario de nombre + contraseña + código
 *   - "invalid"          → mensaje genérico de invitación inválida/expirada
 */
import type React from "react";
import { useCallback, useEffect, useReducer } from "react";
import { useNavigate, useParams } from "react-router";
import {
  PlatformInvitationError,
  type PlatformInvitationPreviewResponse,
  acceptPlatformSignup,
  previewPlatformInvitation,
} from "../../lib/platform-invitation-api";

// ---------------------------------------------------------------------------
// Estado (reducer pattern — mismo patrón que AcceptInvitationPage)
// ---------------------------------------------------------------------------

type PagePhase = "loading" | "signup-required" | "invalid" | "accepted";

interface PageState {
  phase: PagePhase;
  email: string;
  role: string;
  inviterName: string;
  acceptCode: string;
  name: string;
  password: string;
  error: string;
  submitting: boolean;
}

type PageAction =
  | { type: "PREVIEW_OK"; preview: PlatformInvitationPreviewResponse }
  | { type: "PREVIEW_FAIL" }
  | { type: "SET_FIELD"; field: "acceptCode" | "name" | "password"; value: string }
  | { type: "SUBMIT_START" }
  | { type: "SUBMIT_ERROR"; error: string }
  | { type: "ACCEPTED" };

function init(): PageState {
  return {
    phase: "loading",
    email: "",
    role: "",
    inviterName: "",
    acceptCode: "",
    name: "",
    password: "",
    error: "",
    submitting: false,
  };
}

function reducer(state: PageState, action: PageAction): PageState {
  switch (action.type) {
    case "PREVIEW_OK": {
      if (action.preview.mode === "invalid") {
        return { ...state, phase: "invalid" };
      }
      return {
        ...state,
        phase: "signup-required",
        email: action.preview.email ?? "",
        role: action.preview.role ?? "",
        inviterName: action.preview.inviterName ?? "",
      };
    }
    case "PREVIEW_FAIL":
      return { ...state, phase: "invalid" };
    case "SET_FIELD":
      return { ...state, [action.field]: action.value, error: "" };
    case "SUBMIT_START":
      return { ...state, submitting: true, error: "" };
    case "SUBMIT_ERROR":
      return { ...state, submitting: false, error: action.error };
    case "ACCEPTED":
      return { ...state, submitting: false, phase: "accepted" };
    default:
      return state;
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const ROLE_LABELS: Record<string, string> = {
  "studio-admin": "Administrador de estudio",
  "studio-staff": "Staff de estudio",
};

function formatRoleLabel(role: string): string {
  return ROLE_LABELS[role] ?? role;
}

function errorMessageFor(err: PlatformInvitationError): string {
  if (err.code === "INVITATION_INVALIDATED") {
    return "La invitación fue bloqueada por demasiados intentos incorrectos.";
  }
  if (err.code === "USER_EXISTS") {
    return "Ya existe una cuenta con ese email. Contactá a tu administrador para que te asigne el rol directamente.";
  }
  if (err.status === 429) {
    return "Demasiados intentos. Probá de nuevo más tarde.";
  }
  return err.message || "Error inesperado. Reintentá más tarde.";
}

// ---------------------------------------------------------------------------
// Componente
// ---------------------------------------------------------------------------

export const AcceptPlatformInvitationPage: React.FC = () => {
  const { invitationId } = useParams<{ invitationId: string }>();
  const navigate = useNavigate();
  const [state, dispatch] = useReducer(reducer, undefined, init);

  // --- Preview al montar ---
  useEffect(() => {
    if (!invitationId) {
      dispatch({ type: "PREVIEW_FAIL" });
      return;
    }
    let cancelled = false;
    previewPlatformInvitation(invitationId)
      .then((preview) => {
        if (!cancelled) dispatch({ type: "PREVIEW_OK", preview });
      })
      .catch(() => {
        if (!cancelled) dispatch({ type: "PREVIEW_FAIL" });
      });
    return () => {
      cancelled = true;
    };
  }, [invitationId]);

  // --- Submit signup ---
  const handleSignup = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!invitationId) return;

      if (state.name.trim().length === 0 || state.name.trim().length > 120) {
        dispatch({ type: "SUBMIT_ERROR", error: "El nombre es requerido (máx. 120 caracteres)." });
        return;
      }
      if (state.password.length < 8) {
        dispatch({
          type: "SUBMIT_ERROR",
          error: "La contraseña debe tener al menos 8 caracteres.",
        });
        return;
      }
      if (state.acceptCode.length !== 10) {
        dispatch({
          type: "SUBMIT_ERROR",
          error: "El código de aceptación debe tener 10 caracteres.",
        });
        return;
      }

      dispatch({ type: "SUBMIT_START" });
      try {
        const result = await acceptPlatformSignup({
          invitationId,
          acceptCode: state.acceptCode,
          name: state.name.trim(),
          password: state.password,
        });
        dispatch({ type: "ACCEPTED" });
        navigate(result.redirectTo || "/", { replace: true });
      } catch (err) {
        if (err instanceof PlatformInvitationError) {
          dispatch({ type: "SUBMIT_ERROR", error: errorMessageFor(err) });
        } else {
          dispatch({ type: "SUBMIT_ERROR", error: "Error de conexión. Probá de nuevo." });
        }
      }
    },
    [invitationId, state.name, state.password, state.acceptCode, navigate],
  );

  // ---------------------------------------------------------------------------
  // Render helpers
  // ---------------------------------------------------------------------------

  const Header: React.FC = () => (
    <>
      <span className="inv-logo">◈ Tagliapietra Portal</span>
      {state.email && (
        <div className="inv-org">
          <span className="inv-org-label">Invitación al equipo Tagliapietra</span>
          <strong className="inv-org-name">{state.email}</strong>
          <span className="inv-inviter">
            Rol: {formatRoleLabel(state.role)}
            {state.inviterName ? ` · invitado por ${state.inviterName}` : ""}
          </span>
        </div>
      )}
    </>
  );

  const ErrorMessage: React.FC = () =>
    state.error ? <div className="inv-msg error">{state.error}</div> : null;

  // ---------------------------------------------------------------------------
  // Vistas por fase
  // ---------------------------------------------------------------------------

  if (state.phase === "loading") {
    return (
      <div className="inv-container">
        <div className="inv-card">
          <span className="inv-logo">◈ Tagliapietra Portal</span>
          <div className="inv-loading">
            <div className="spinner" />
            <p>Cargando invitación…</p>
          </div>
        </div>
        <InvStyles />
      </div>
    );
  }

  if (state.phase === "invalid") {
    return (
      <div className="inv-container">
        <div className="inv-card">
          <span className="inv-logo">◈ Tagliapietra Portal</span>
          <h2>Invitación no válida</h2>
          <div className="inv-msg error">
            Esta invitación no es válida, ya fue usada o expiró. Pedí una nueva invitación al
            administrador de la plataforma.
          </div>
        </div>
        <InvStyles />
      </div>
    );
  }

  if (state.phase === "accepted") {
    return (
      <div className="inv-container">
        <div className="inv-card">
          <Header />
          <h2>Invitación aceptada</h2>
          <div className="inv-msg notice">Bienvenido/a al equipo Tagliapietra. Redirigiendo…</div>
        </div>
        <InvStyles />
      </div>
    );
  }

  // phase === "signup-required"
  return (
    <div className="inv-container">
      <form className="inv-card" onSubmit={handleSignup} noValidate>
        <Header />
        <h2>Crear cuenta</h2>
        <p className="inv-sub">Completá los datos para aceptar la invitación.</p>
        <ErrorMessage />

        <div className="form-group">
          <label htmlFor="name">Nombre completo</label>
          <input
            id="name"
            type="text"
            value={state.name}
            onChange={(e) => dispatch({ type: "SET_FIELD", field: "name", value: e.target.value })}
            maxLength={120}
            autoComplete="name"
            placeholder="Tu nombre"
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="password">Contraseña</label>
          <input
            id="password"
            type="password"
            value={state.password}
            onChange={(e) =>
              dispatch({ type: "SET_FIELD", field: "password", value: e.target.value })
            }
            autoComplete="new-password"
            minLength={8}
            placeholder="Mínimo 8 caracteres"
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="acceptCode">Código de aceptación</label>
          <input
            id="acceptCode"
            type="text"
            value={state.acceptCode}
            onChange={(e) =>
              dispatch({ type: "SET_FIELD", field: "acceptCode", value: e.target.value })
            }
            maxLength={10}
            autoComplete="one-time-code"
            placeholder="10 caracteres"
            required
          />
          <p className="field-hint">Encontrás el código en el email de invitación.</p>
        </div>

        <button type="submit" className="inv-submit" disabled={state.submitting}>
          {state.submitting ? "Creando cuenta…" : "Crear cuenta y aceptar"}
        </button>
      </form>
      <InvStyles />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Estilos — mismos tokens que AcceptInvitationPage / LoginForm
// ---------------------------------------------------------------------------

const InvStyles: React.FC = () => (
  <style>{`
    .inv-container {
      display: flex;
      justify-content: center;
      align-items: center;
      min-height: 100vh;
      background: var(--color-bg);
      font-family: var(--font-sans);
      padding: 24px 16px;
      box-sizing: border-box;
    }
    .inv-card {
      background: var(--color-surface);
      border: 1px solid var(--color-border);
      padding: 32px;
      border-radius: 10px;
      width: 100%;
      max-width: 420px;
      box-sizing: border-box;
    }
    .inv-logo {
      font-family: var(--font-mono);
      font-size: 13px;
      font-weight: 600;
      letter-spacing: .15em;
      color: var(--color-accent);
      display: block;
      margin-bottom: 16px;
    }
    .inv-org {
      display: flex;
      flex-direction: column;
      gap: 2px;
      margin-bottom: 16px;
      padding: 10px 12px;
      background: var(--color-surface-2);
      border: 1px solid var(--color-border);
      border-radius: 6px;
    }
    .inv-org-label {
      font-size: 11px;
      color: var(--color-muted);
      text-transform: uppercase;
      letter-spacing: .08em;
    }
    .inv-org-name {
      font-size: 15px;
      color: var(--color-text);
      font-weight: 600;
    }
    .inv-inviter {
      font-size: 12px;
      color: var(--color-muted);
    }
    .inv-card h2 {
      margin: 0 0 4px;
      font-size: 18px;
      color: var(--color-text);
    }
    .inv-sub {
      color: var(--color-muted);
      font-size: 12px;
      margin: 0 0 20px;
    }
    .inv-loading {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 12px;
      padding: 24px 0;
      color: var(--color-muted);
      font-size: 13px;
    }
    .inv-msg {
      padding: 10px 12px;
      border-radius: 6px;
      margin-bottom: 16px;
      font-size: 13px;
      line-height: 1.5;
    }
    .inv-msg.error {
      background: rgba(248, 113, 113, .12);
      color: var(--color-danger);
      border: 1px solid rgba(248, 113, 113, .3);
    }
    .inv-msg.notice {
      background: rgba(52, 211, 153, .12);
      color: var(--color-success);
      border: 1px solid rgba(52, 211, 153, .3);
    }
    .form-group { margin-bottom: 16px; }
    .form-group label {
      display: block;
      margin-bottom: 6px;
      color: var(--color-muted);
      font-size: 12px;
    }
    .form-group input {
      width: 100%;
      padding: 10px 12px;
      background: var(--color-surface-2);
      border: 1px solid var(--color-border);
      border-radius: 6px;
      color: var(--color-text);
      font-size: 14px;
      box-sizing: border-box;
    }
    .form-group input:focus {
      outline: none;
      border-color: var(--color-accent);
    }
    .field-hint {
      margin: 4px 0 0;
      font-size: 11px;
      color: var(--color-muted);
    }
    .inv-submit {
      width: 100%;
      padding: 10px;
      margin-top: 4px;
      background: var(--color-accent);
      color: #0b1220;
      border: none;
      border-radius: 6px;
      cursor: pointer;
      font-weight: 600;
      font-size: 14px;
    }
    .inv-submit:disabled { opacity: .5; cursor: default; }
  `}</style>
);
